<template>
  <div
    class="position-absolute w-100 dashboard-navbar-root"
    ref="navbarWrapper"
    data-dashboard-navbar-root
  >
    <div
      ref="navbarScrollArea"
      class="d-flex justify-content-start overflow-x-auto py-xl-2 py-3 ps-3 pe-2 mt-0 navbar-container dashboard-navbar__scroll"
    >
      <div class="d-flex flex-nowrap align-items-center navbar-items-row">
        <div
          v-for="navbar in filteredNavbarData"
          :key="navItemKey(navbar)"
          class="navbar-item-stack d-flex flex-shrink-0"
          :class="{ 'navbar-item-stack--open': navbar.dropdownOpen }"
        >
          <!-- Main nav item -->
          <button
            v-if="navbar.dropdown"
            type="button"
            class="dashboard-navbar__link dashboard-navbar__link--trigger dropdown-toggle"
            :class="{
              'dashboard-navbar__link--active': isActiveNavbar(navbar),
              'dashboard-navbar__link--open': navbar.dropdownOpen,
            }"
            :aria-expanded="navbar.dropdownOpen ? 'true' : 'false'"
            @click="toggleDropdown(navbar._idx, $event)"
          >
            <i
              :class="[
                navbar.icon,
                'dashboard-navbar__icon',
                isActiveNavbar(navbar) || navbar.dropdownOpen
                  ? 'text-white'
                  : 'text-primary',
              ]"
            ></i>
            <span
              class="dashboard-navbar__label"
              :class="{
                'text-white':
                  isActiveNavbar(navbar) || navbar.dropdownOpen,
              }"
            >
              {{ navbar.title }}
            </span>
          </button>

          <router-link
            v-else
            :to="{ name: navbar.name }"
            class="dashboard-navbar__link"
            :class="{ 'dashboard-navbar__link--active': isActiveNavbar(navbar) }"
          >
            <i
              :class="[
                navbar.icon,
                'dashboard-navbar__icon',
                isActiveNavbar(navbar) ? 'text-white' : 'text-primary',
              ]"
            ></i>
            <span
              class="dashboard-navbar__label"
              :class="{ 'text-white': isActiveNavbar(navbar) }"
            >
              {{ navbar.title }}
            </span>
          </router-link>

          <!-- Dropdown menu: fixed + high z-index via inline style when open -->
          <ul
            v-if="navbar.dropdown"
            class="dropdown-menu dashboard-navbar__dropdown border-0"
            data-dropdown-panel
            :class="{ show: navbar.dropdownOpen }"
            :style="navbar.dropdownStyle"
            role="menu"
          >
            <li
              v-for="(dropdownItem, subIndex) in navbar.dropdown"
              :key="subIndex"
              class="dashboard-navbar__dropdown-li"
            >
              <!-- Nested dropdown trigger -->
              <button
                v-if="dropdownItem.dropdownItems"
                type="button"
                class="toggle-dropdown-items dashboard-navbar__subtoggle border-0 dropdown-toggle w-100"
                @click.stop="toggleDropdownItems(navbar._idx, subIndex, $event)"
              >
                {{ dropdownItem.title }}
              </button>
              <!-- Regular dropdown item -->
              <button
                v-else
                type="button"
                class="dropdown-item dashboard-navbar__dropdown-item"
                @click="handleDropdownClick(dropdownItem.name)"
              >
                {{ dropdownItem.title }}
              </button>
              <!-- Nested dropdown items -->
              <ul
                v-if="dropdownItem.dropdownItems"
                class="dropdown-items dashboard-navbar__nested border-top"
                :class="{ show: dropdownItem.dropdownItemsOpen }"
              >
                <li
                  v-for="(
                    subDropdownItem, subItemIndex
                  ) in dropdownItem.dropdownItems"
                  :key="subItemIndex"
                >
                  <button
                    type="button"
                    @click="handleDropdownClick(subDropdownItem.name)"
                    class="dropdown-item dashboard-navbar__dropdown-item"
                  >
                    {{ subDropdownItem.title }}
                  </button>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Root: avoid accidental clipping of descendants (dropdowns use position: fixed) */
.dashboard-navbar-root {
  overflow: visible;
}

.navbar-container {
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

.dashboard-navbar__scroll {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.8) inset;
}

/* Flex row: each slot is an isolated stacking context so siblings don’t paint over each other’s clicks */
.navbar-items-row {
  gap: 0.35rem;
}

@media (min-width: 1200px) {
  .navbar-items-row {
    gap: 0.5rem;
  }
}

/*
  Stacking: later siblings normally paint on top for overlapping regions (same z-index).
  When a column’s dropdown is open, lift that entire column above neighbors so its trigger
  and panel are never masked by another nav group (role-safe, order-independent).
*/
.navbar-item-stack {
  position: relative;
  z-index: 1;
  isolation: isolate;
}

.navbar-item-stack--open {
  z-index: 1060;
}

.navbar-container::-webkit-scrollbar {
  height: 4px;
}

.navbar-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.dashboard-navbar__link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.85rem;
  border: none;
  background: transparent;
  text-decoration: none;
  color: #495057;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  border-radius: 0.5rem;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;
}

.dashboard-navbar__link:hover {
  color: #0d6efd;
  background-color: rgba(13, 110, 253, 0.08);
  text-decoration: none;
}

.dashboard-navbar__link:focus-visible {
  outline: 2px solid rgba(13, 110, 253, 0.45);
  outline-offset: 2px;
}

.dashboard-navbar__link--active {
  color: #fff !important;
  background-color: #0d6efd !important;
  box-shadow: 0 2px 8px rgba(13, 110, 253, 0.35);
}

.dashboard-navbar__link--active:hover {
  color: #fff !important;
  background-color: #0b5ed7 !important;
}

.dashboard-navbar__link--trigger.dashboard-navbar__link--open:not(
    .dashboard-navbar__link--active
  ) {
  color: #0d6efd;
  background-color: rgba(13, 110, 253, 0.12);
}

.dashboard-navbar__link--trigger::after {
  margin-left: 0.15rem;
  vertical-align: 0.15em;
  opacity: 0.75;
}

.dashboard-navbar__icon {
  font-size: 1rem !important;
  flex-shrink: 0;
}

.dashboard-navbar__label {
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-menu {
  display: none;
  min-width: 220px;
  pointer-events: auto;
}

.dropdown-menu.show {
  display: block;
}

.dashboard-navbar__dropdown {
  padding: 0.4rem 0;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
  border-radius: 0.625rem !important;
  box-shadow:
    0 10px 40px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.04);
}

.dashboard-navbar__dropdown-li {
  list-style: none;
}

.dashboard-navbar__dropdown-item {
  width: calc(100% - 0.5rem);
  margin-left: 0.25rem;
  margin-right: 0.25rem;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 0.375rem;
  background: none;
  cursor: pointer;
  white-space: nowrap;
  text-align: left;
  font-size: 0.875rem;
  color: #343a40;
  transition: background-color 0.12s ease, color 0.12s ease;
}

.dashboard-navbar__dropdown-item:hover {
  background-color: rgba(13, 110, 253, 0.08);
  color: #0d6efd;
}

.dropdown-items {
  display: none;
  list-style: none;
  padding: 0.25rem 0 0.35rem 0.75rem;
  margin: 0;
}

.dropdown-items.show {
  display: block;
}

.dashboard-navbar__nested {
  border-color: rgba(0, 0, 0, 0.06) !important;
}

.toggle-dropdown-items {
  background: none;
  cursor: pointer;
  text-align: left;
  padding: 0.5rem 0.75rem;
  margin: 0 0.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.875rem;
  font-weight: 500;
  color: #343a40;
  border-radius: 0.375rem;
  transition: background-color 0.12s ease, color 0.12s ease;
}

.toggle-dropdown-items:hover {
  background-color: rgba(13, 110, 253, 0.06);
  color: #0d6efd;
}

.bi-logo {
  font-size: 23px !important;
  margin: 0;
}

@media (max-width: 767px) {
  .dashboard-navbar__icon {
    font-size: 1.25rem !important;
  }

  .dashboard-navbar__link {
    padding: 0.5rem 0.65rem;
  }
}
</style>

<script>
import { mapGetters } from "vuex";
import navbar from "../../data/navbar/index.json";

export default {
  data() {
    return {
      navbarData: this.initializeNavbarData(navbar),
    };
  },
  watch: {
    // Close all dropdowns when route changes
    $route() {
      this.closeAllDropdowns();
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside, true);
    this.$nextTick(() => {
      const el = this.$refs.navbarScrollArea;
      if (el) {
        el.addEventListener("scroll", this.closeAllDropdowns, { passive: true });
      }
    });
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside, true);
    const el = this.$refs.navbarScrollArea;
    if (el) {
      el.removeEventListener("scroll", this.closeAllDropdowns);
    }
  },
  methods: {
    initializeNavbarData(navbarData) {
      return navbarData.map((item) => {
        const newItem = { ...item, dropdownOpen: false, dropdownStyle: null };
        if (newItem.dropdown) {
          newItem.dropdown = newItem.dropdown.map((dropdownItem) => ({
            ...dropdownItem,
            dropdownItemsOpen: false,
          }));
        }
        return newItem;
      });
    },
    toggleDropdown(index, event) {
      if (event) {
        event.stopPropagation();
      }

      const isCurrentlyOpen = this.navbarData[index].dropdownOpen;

      // Close all dropdowns first
      this.navbarData.forEach((item, i) => {
        this.$set(this.navbarData[i], "dropdownOpen", false);
        this.$set(this.navbarData[i], "dropdownStyle", null);
        if (item.dropdown) {
          item.dropdown.forEach((dropdownItem, j) => {
            if (dropdownItem.dropdownItemsOpen) {
              this.$set(
                this.navbarData[i].dropdown[j],
                "dropdownItemsOpen",
                false
              );
            }
          });
        }
      });

      // If it was closed, open it with fixed positioning anchored to the button
      if (!isCurrentlyOpen) {
        const btn = event.currentTarget;
        const rect = btn.getBoundingClientRect();
        this.$set(this.navbarData[index], "dropdownStyle", {
          position: "fixed",
          top: `${Math.round(rect.bottom)}px`,
          left: `${Math.round(rect.left)}px`,
          zIndex: 1080,
        });
        this.$set(this.navbarData[index], "dropdownOpen", true);
      }
    },
    toggleDropdownItems(navbarIndex, dropdownIndex, event) {
      // Prevent event from bubbling
      if (event) {
        event.stopPropagation();
      }

      const navbar = this.navbarData[navbarIndex];
      const dropdownItem = navbar.dropdown[dropdownIndex];
      this.$set(navbar.dropdown, dropdownIndex, {
        ...dropdownItem,
        dropdownItemsOpen: !dropdownItem.dropdownItemsOpen,
      });
    },
    async handleDropdownClick(name) {
      if (name) {
        // Close all dropdowns before navigation
        this.closeAllDropdowns();
        // Small delay to ensure dropdown closes before navigation
        await this.$nextTick();
        this.$router.push({ name });
      }
    },
    closeDropdown(index) {
      this.$set(this.navbarData[index], "dropdownOpen", false);
      const navbar = this.navbarData[index];
      if (navbar.dropdown) {
        navbar.dropdown.forEach((item, i) => {
          if (item.dropdownItemsOpen) {
            this.$set(navbar.dropdown[i], "dropdownItemsOpen", false);
          }
        });
      }
    },
    closeAllDropdowns() {
      this.navbarData.forEach((item, i) => {
        if (item.dropdownOpen) {
          this.$set(this.navbarData[i], "dropdownOpen", false);
          this.$set(this.navbarData[i], "dropdownStyle", null);
        }
        if (item.dropdown) {
          item.dropdown.forEach((dropdownItem, j) => {
            if (dropdownItem.dropdownItemsOpen) {
              this.$set(
                this.navbarData[i].dropdown[j],
                "dropdownItemsOpen",
                false
              );
            }
          });
        }
      });
    },
    handleClickOutside(event) {
      const root = this.$refs.navbarWrapper;
      if (root && root.contains(event.target)) {
        return;
      }
      this.closeAllDropdowns();
    },
    /** Stable key for v-for: role/filter order must not reuse wrong DOM nodes */
    navItemKey(navbar) {
      const idx = navbar && navbar._idx !== undefined ? navbar._idx : "x";
      const name = navbar && navbar.name ? String(navbar.name) : "item";
      return `nav-${name}-${idx}`;
    },
    getUserRole() {
      if (
        !this.authData ||
        !this.authData.role ||
        !this.authData.role.fullName
      ) {
        return "";
      }
      return this.authData.role.fullName;
    },

    isActiveNavbar(navbar) {
      const currentRouteName = String(this.$route.name || "");
      const currentRoutePath = String(this.$route.path || "");

      // Helper function for precise route matching (no substring matching)
      const isRouteMatch = (matchName, matchSlug) => {
        if (!matchName && !matchSlug) return false;

        // Exact route name match
        if (matchName && currentRouteName === matchName) {
          return true;
        }

        // Exact path match
        if (matchSlug && currentRoutePath === matchSlug) {
          return true;
        }

        // Child route: route name starts with matchName + separator (e.g., "payments-index")
        if (matchName && currentRouteName) {
          const separators = ["-", "_"];
          for (const sep of separators) {
            if (currentRouteName.startsWith(matchName + sep)) {
              return true;
            }
          }
        }

        // Child path: path starts with matchSlug + "/" (e.g., "/payments/list")
        if (matchSlug && currentRoutePath && matchSlug !== "/") {
          if (currentRoutePath.startsWith(matchSlug + "/")) {
            return true;
          }
        }

        return false;
      };

      // First, check all dropdown items (they take priority)
      if (navbar.dropdown && Array.isArray(navbar.dropdown)) {
        for (const item of navbar.dropdown) {
          // Check dropdown item
          if (isRouteMatch(item.name, item.slug)) {
            return true;
          }

          // Check nested dropdown items
          if (item.dropdownItems && Array.isArray(item.dropdownItems)) {
            for (const sub of item.dropdownItems) {
              if (isRouteMatch(sub.name, sub.slug)) {
                return true;
              }
            }
          }
        }
      }

      // Then check main navbar item (only if no dropdown items matched)
      if (isRouteMatch(navbar.name, navbar.slug)) {
        return true;
      }

      return false;
    },
  },
  computed: {
    ...mapGetters("auth", {
      authData: "getAuthData",
    }),
    filteredNavbarData() {
      const userRole = this.getUserRole();

      if (!userRole) {
        return [];
      }

      const roleMap = {
        Admin: (item) => item.is_admin === true,
        Caissier: (item) => item.is_cashier === true,
        Magasinier: (item) => item.is_storekeeper === true,
        Commercial: (item) => item.is_commercial === true,
        Comptable: (item) => item.is_accountant === true,
        "Aide Comptable": (item) => item.is_accountant === true,
        Importation: (item) => item.is_importation === true,
        Reparation: (item) => item.is_repair === true,
        IT: (item) => item.is_it === true,
      };

      if (!roleMap[userRole]) {
        return [];
      }

      const roleFilter = roleMap[userRole];

      return this.navbarData
        .map((item, i) => {
          // Top-level row must pass the role flag (e.g. is_importation === true for Importation).
          // Do not show a parent only because a child matched — avoids wrong sections and overlap.
          if (!roleFilter(item)) {
            return null;
          }

          const filteredDropdown = item.dropdown
            ? item.dropdown.filter(roleFilter)
            : null;

          const hasValidDropdown =
            filteredDropdown && filteredDropdown.length > 0;

          if (item.dropdown) {
            if (!hasValidDropdown) {
              return null;
            }
            return { ...item, dropdown: filteredDropdown, _idx: i };
          }

          const { dropdown, ...rest } = item;
          return { ...rest, _idx: i };
        })
        .filter((item) => item !== null);
    },
  },
};
</script>
